import React, { useCallback, useState, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Eye } from 'lucide-react';
interface Page {
  src: string;
  label: string;
}
const pages: Page[] = [{
  src: 'https://cdn.magicpatterns.com/uploads/uYtjX9guGYBWiFmoEZSLoC/Volumen_1_(7).webp',
  label: 'Foundation Phase — Session 2: Traffic Lights'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/nkfWiKkSp4AnASV9x4q1ow/volumen_2_(9).webp',
  label: 'Skill Development — Session 16: Shooting Basics'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/uv5hCz8KrkM6wuhwy1MQjD/volumen_3_(14).webp',
  label: 'Game Intelligence — Session 33: Attacking Space'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/pKuLACchBQP5Aezaygryn2/volumen_4_(13).webp',
  label: 'Team Development — Session 44: Transition To Attack'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/vH6qoxt3MXivu8Ktfi6Ur1/VOLUMEN_5_(7).webp',
  label: 'The 365 Session Vault — Emergency Small Space Session'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/1e1oURDL6Wa937LnytW5E7/volumen_6_(25).webp',
  label: 'Coach Management System — Equipment Inventory Sheet'
}];
const variants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 80 : -80,
    opacity: 0
  }),
  center: {
    x: 0,
    opacity: 1
  },
  exit: (direction: number) => ({
    x: direction > 0 ? -80 : 80,
    opacity: 0
  })
};
export function InsidePreview() {
  const [[index, direction], setState] = useState<[number, number]>([0, 0]);
  const paginate = useCallback((newDirection: number) => {
    setState(([prev]) => {
      const next = (prev + newDirection + pages.length) % pages.length;
      return [next, newDirection];
    });
  }, []);
  const goTo = useCallback((target: number) => {
    setState(([prev]) => [target, target > prev ? 1 : -1]);
  }, []);
  const current = pages[index];
  return <section className="py-24 bg-navy-950 relative overflow-hidden border-t border-navy-800" aria-roledescription="carousel" aria-label="Look inside the Football Ready System" data-id="element-103" data-mp="w82g1eznw0pe">
      <div className="absolute inset-0 bg-stadium-glow opacity-40 pointer-events-none" data-id="element-104" data-mp="pqr7sfdzo9mj"></div>

      <div className="container mx-auto px-4 relative z-10" data-id="element-105" data-mp="ktr8qofk474x">
        <div className="max-w-3xl mx-auto text-center mb-14" data-id="element-106" data-mp="fuo1qyg0nqub">
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-500/15 text-green-400 mb-6" data-id="element-107" data-mp="fwipslml3z2m">
            <Eye className="w-8 h-8" data-id="element-108" data-mp="yr61a213wv14" />
          </motion.div>
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="font-anton text-4xl md:text-6xl text-white uppercase tracking-wide mb-4" data-id="element-109" data-mp="1w963dlkammwv">
            LOOK INSIDE THE SYSTEM
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-xl text-gray-300" data-id="element-110" data-mp="28t36i88exgju">
            See exactly what you get — real sessions, drills, and coaching tools
            from inside all 6 volumes.
          </motion.p>
        </div>

        <div className="max-w-md mx-auto" data-id="element-111" data-mp="31g7e8kyw48zb">
          <div className="relative flex items-center justify-center" data-id="element-112" data-mp="31h8ncjwcilee">
            {/* Prev */}
            <button onClick={() => paginate(-1)} aria-label="Previous page" className="absolute left-0 -translate-x-1/2 md:-translate-x-1/2 z-20 w-12 h-12 rounded-full bg-navy-800 border border-navy-700 hover:border-gold-500 text-white flex items-center justify-center transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500 shadow-lg" data-id="element-113" data-mp="36fa6t5xoomqe">
              <ChevronLeft className="w-6 h-6" data-id="element-114" data-mp="btkobaoq8qpx" />
            </button>

            {/* Stage */}
            <div className="relative w-full aspect-[2/3] overflow-hidden rounded-2xl border border-navy-700 bg-navy-900 shadow-2xl" data-id="element-115" data-mp="3k9dxt5kn51xh">
              <div className="absolute -inset-2 bg-gold-500/10 blur-2xl pointer-events-none" data-id="element-116" data-mp="3t8hey408z79c"></div>
              <AnimatePresence custom={direction} mode="popLayout" initial={false} data-id="element-117" data-mp="3p5uvorhn6unr">
                <motion.img key={index} src={current.src} alt={current.label} loading="lazy" decoding="async" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
                duration: 0.35,
                ease: 'easeInOut'
              }} className="absolute inset-0 w-full h-full object-contain" data-id="element-118" data-mp="18fxh83w6i8pc" />
              </AnimatePresence>
            </div>

            {/* Next */}
            <button onClick={() => paginate(1)} aria-label="Next page" className="absolute right-0 translate-x-1/2 md:translate-x-1/2 z-20 w-12 h-12 rounded-full bg-navy-800 border border-navy-700 hover:border-gold-500 text-white flex items-center justify-center transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500 shadow-lg" data-id="element-119" data-mp="1zo7edhztsb46">
              <ChevronRight className="w-6 h-6" data-id="element-120" data-mp="2igiamszz5atb" />
            </button>
          </div>

          {/* Caption */}
          <div className="mt-6 text-center min-h-[3rem]" data-id="element-121" data-mp="2saxohr059a38">
            <span className="text-gold-500 font-oswald text-sm uppercase tracking-widest" data-id="element-122" data-mp="2nce0zm5wjq8r">
              Page {index + 1} of {pages.length}
            </span>
            <p className="text-gray-300 font-medium mt-1" aria-live="polite" data-id="element-123" data-mp="2jcvggrhzth6g">
              {current.label}
            </p>
          </div>

          {/* Dots */}
          <div className="flex items-center justify-center gap-3 mt-6" data-id="element-124" data-mp="29dmq08mc6kou">
            {pages.map((_, i) => <button key={i} onClick={() => goTo(i)} aria-label={`Go to page ${i + 1}`} aria-current={i === index} className={`h-2.5 rounded-full transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500 ${i === index ? 'w-8 bg-gold-500' : 'w-2.5 bg-navy-700 hover:bg-navy-600'}`} data-id="element-125" data-mp="24klb40m4ql59" data-mpi={i} />)}
          </div>
        </div>
      </div>
    </section>;
}